import { Employee, Expense, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Form, Input, Modal } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { Store } from "antd/lib/form/interface"
import { useVendors } from "Shared/hooks/vendor.hooks"
import { zeroPad } from "Shared/utils/helpers"
import SEPAExportConfirmationRules from "Components/forms/rules/sepaExportConfirmationRule"
import { buildBankSubjectLine } from "Shared/utils/banking.utils"
import { useCompany } from "Shared/hooks/company.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"

interface SEPAExportConfirmationModalInterface {
    expenses: Expense[]
    isShowing: boolean
    onCancel: () => void
    onSubmit: (expenses: Expense[], subjectLines: any) => Promise<void>
}
const SEPAExportConfirmationModal: React.FC<SEPAExportConfirmationModalInterface> = ({ expenses, isShowing, onCancel, onSubmit }) => {
    const [form] = Form.useForm()
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const vendors = useVendors()
    const employees = useEmployees()
    const rules = SEPAExportConfirmationRules()
    const company = useCompany()

    // prettier-ignore
    const mappedVendorNames = useMemo(() => vendors.reduce((mapped: any, vendor: Vendor) => {
        mapped[vendor.id] = vendor.name
        return mapped
    }, {}), [vendors])

    const mappedEmployeeNames = useMemo(
        () =>
            employees.reduce((mapped: any, employee: Employee) => {
                mapped[employee.id] = `${employee.firstName} ${employee.lastName}`
                return mapped
            }, {}),
        [employees]
    )

    useEffect(() => {
        form.resetFields()
    }, [isShowing])

    const onFormSubmit = (values: Store) => {
        setIsLoading(true)
        const subjects = values.subjects // This contains an object that acts as a key-value pair: {expenseId: subjectLine}
        onSubmit(expenses, subjects).finally(handleHide)
    }

    const handleHide = () => {
        setIsLoading(false)
        form.resetFields()
        onCancel()
    }

    const getVendorName = (expense: Expense) => {
        if (!expense.vendor) return ""

        if (expense.vendorType === VendorTypeEnum.VENDOR) return mappedVendorNames[expense.vendor]

        return mappedEmployeeNames[expense.vendor]
    }

    return (
        <Modal
            title={t("label:export_sepa")}
            visible={isShowing}
            maskClosable={false}
            onOk={form.submit}
            onCancel={handleHide}
            okText={t("action:download_sepa")}
            keyboard
            destroyOnClose
            confirmLoading={isLoading}
        >
            <Form form={form} layout="vertical" onFinish={onFormSubmit}>
                {expenses.map((expense, index) => {
                    const initialSubjectLine = expense.sepaXmlSubjectLine ?? buildBankSubjectLine((company as any).customBankSubjectParts, expense)
                    return (
                        <Form.Item
                            key={index}
                            label={`${t(`label:subject_line`)}${expense.expenseNumber} (${getVendorName(expense)})`}
                            name={["subjects", expense.id]}
                            initialValue={initialSubjectLine}
                            rules={rules.subjectLine}
                        >
                            <Input />
                        </Form.Item>
                    )
                })}
            </Form>
        </Modal>
    )
}

export default SEPAExportConfirmationModal
